import { Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import img2xBlack from 'img/tooltip/mailspace-dark2x.png';
import img2x from 'img/tooltip/mailspace-light2x.png';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { fullTariff, trialTariff } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { isPublicFolder } from 'reactApp/modules/storage/folder.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { FloatingTooltip } from 'reactApp/ui//FloatingTooltip/FloatingTooltip';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { ETooltipAppearance, ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './DownloadLimitsPromo.css';

export interface DownloadLimitsPromoProps {
    targetRef: any;
    onShow?: () => void;
    onClose?: () => void;
    isDarkMode?: boolean;
    item: any;
    owner?: boolean;
    isContextMenu?: boolean;
}

export const DownloadLimitsPromo = memo(
    ({ targetRef, onClose, onShow, isDarkMode, item, owner, isContextMenu }: DownloadLimitsPromoProps): any => {
        const dispatch = useDispatch();
        const isMobile = EnvironmentSelectors.isMobile();
        const [show, setShow] = useState(false);
        const isUserWithoutPayment = useSelector(PaidInfoSelectors.isUserWithoutPayment);
        const source = `limit_sharing_${isPublicFolder(item) ? 'folder_public' : 'one_public'}`;
        const email = useSelector(UserSelectors.getEmail);
        const productId = isUserWithoutPayment ? trialTariff : fullTariff;

        const sendAnalytics = (action) => {
            sendDwh({
                eventCategory: 'tooltip-limit-sharing',
                action,
                dwhData: { place: isPublicFolder(item) ? 'folder_public' : 'one_public' },
            });
        };

        useEffect(() => {
            dispatch(loadPaidInfoRequest());
            emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_LINK_SHOW);
            sendAnalytics('show');
            setShow(true);
            onShow?.();
        }, []);

        const handleClose = useCallback(() => {
            emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_LINK_CLOSE);
            sendAnalytics('close');
            setShow(false);
            onClose?.();
        }, [onClose]);

        const handleClick = useCallback(() => {
            emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_LINK_CLICK);
            sendAnalytics('click');
            dispatch(
                loadProductsAndOpenFastCheckout({
                    productId,
                    paySource: source,
                    title: 'Подписка Mail Space: скачивания без лимита и другие опции',
                    hideOtherTariffs: true,
                })
            );
            setShow(false);
            onClose?.();
        }, [dispatch, productId, onClose]);

        const handleClickAuth = () => {
            const path = window.location.pathname;
            const successPage = `${window.location.origin}${path}`;

            window.__PH.authForm.show({
                formType: 'login',
                successPage,
                email,
            });

            window.__PH?.authForm.show({ source: 'cloud-public', from: 'navi' });
        };

        if (!show) {
            return null;
        }

        return (
            show && (
                <FloatingTooltip
                    target={targetRef}
                    // placement={owner ? ETooltipPlacement.bottom : ETooltipPlacement.bottomEnd}
                    placement={isContextMenu ? ETooltipPlacement.left : owner ? ETooltipPlacement.bottom : ETooltipPlacement.bottomEnd}
                    closable
                    closeOnOutsideClick={false}
                    onClose={handleClose}
                    usePortal
                    closeOnScroll
                    appearance={isDarkMode ? ETooltipAppearance.dark : undefined}
                    allowFlip={!!owner}
                >
                    <div className={classNames(styles.tooltip, { [styles.tooltipMobile]: isMobile })}>
                        <Title className={classNames(styles.tooltipTitle, { [styles.tooltipTitleMobile]: isMobile })}>
                            Скачайте с&nbsp;подпиской Mail&nbsp;Space
                        </Title>
                        <Spacing size={4} />
                        <Text className={classNames(styles.tooltipText, { [styles.tooltipTextMobile]: isMobile })}>
                            Достигнут лимит скачиваний. Снять <br />
                            его можно с&nbsp;подпиской&nbsp;&mdash; расскажите владельцу или оформите для себя
                        </Text>
                        <Spacing size={8} />
                        {email ? (
                            <Button onClick={handleClick} sizeMode={ButtonSizeMode.small} theme="vk" className={styles.button}>
                                {isUserWithoutPayment ? 'Подключить за 1 ₽' : 'Подключить'}
                            </Button>
                        ) : (
                            <Button onClick={handleClickAuth} sizeMode={ButtonSizeMode.small} theme="vk" className={styles.button}>
                                Авторизоваться
                            </Button>
                        )}
                        <img
                            src={isDarkMode ? img2xBlack : img2x}
                            alt="Снять лимит на скачивание с пабликов"
                            className={classNames(styles.image, { [styles.imageMobile]: isMobile })}
                        />
                    </div>
                </FloatingTooltip>
            )
        );
    }
);

DownloadLimitsPromo.displayName = 'DownloadLimitsPromo';
